import { createSlice } from '@reduxjs/toolkit'
import { 
  getProductSystemNameByAlias, 
  RESOLVE_FUNDING_OPTIONS_CONTINGENCY, 
  EVALUATE_FUNDING_OPTIONS,
} from 'universal'


const initialState = {
  // Funding Instrument Flow Query Parameters.
  flowInputParams: {
    productName: null,
    returnUri: null,
    cancelUri: null,
    webviewCallback: null,
  },

  secondaryFundingOptions: [],

  // selected funding instrument id from existing instruments.
  selectedFundingInstrumentId: null,

  // funding instrument ids array from existing instruments.
  fundingInstrumentIds: [],

  // Funding Instrument ID to Instrument map (existing instruments).
  fundingInstrumentsMap: {},

  // Query params for linking new funding instruments
  linkFIflowQuery: {},

  statusDetailsValues: [],
  hasRiskStepUpContingency: false,
  hasRiskDeclineContingency: false,
  hasNegativeBalanceContingency: false,
  isFundingSourcesUnavailable: false,
  toastMessage: {
    message: null,
    isSuccess: true,
  },
}

const fundingInstrumentsSlice = createSlice({
  name: 'fundingInstruments',
  initialState,
  reducers: {
    initFundingInstruments(state, action) {
      const {
        secondaryFundingOptions = [],
        productName = null,
        fundingInstruments = [],
        returnUri = null,
        cancelUri = null,
        webviewCallback = null,
        linkFIflowQuery = {},
        statusDetailsValues = [],
        hasRiskStepUpContingency = false,
        hasRiskDeclineContingency = false,
        hasNegativeBalanceContingency = false,
        isFundingSourcesUnavailable = false,
      } = action.payload || {}

      state.flowInputParams = {
        productName : getProductSystemNameByAlias(productName),
        returnUri,
        cancelUri,
        webviewCallback,
      }

      state.secondaryFundingOptions = secondaryFundingOptions

      state.fundingInstrumentsMap = state.fundingInstrumentsMap || {}
      state.fundingInstrumentIds = state.fundingInstrumentIds || []

      let defaultSelectedInstrument = fundingInstruments.find(
        instrument => instrument?.id && instrument.status === EVALUATE_FUNDING_OPTIONS.STATUS.ELIGIBLE
      )

      // Take any instrument if no eligible instrument found
      if (!defaultSelectedInstrument && fundingInstruments.length > 0) {
        defaultSelectedInstrument = fundingInstruments[0]
      }

      state.selectedFundingInstrumentId = defaultSelectedInstrument?.id

      // Populate the fi map and ids in order
      fundingInstruments.forEach(instrument => {
        if (instrument?.id) {
          state.fundingInstrumentsMap[instrument.id] = instrument
          if (!state.fundingInstrumentIds.includes(instrument.id)) {
            state.fundingInstrumentIds.push(instrument.id)
          }
        }
      })

      state.linkFIflowQuery = linkFIflowQuery
      
      state.statusDetailsValues = statusDetailsValues
      state.hasRiskStepUpContingency = hasRiskStepUpContingency
      state.hasRiskDeclineContingency = hasRiskDeclineContingency
      state.hasNegativeBalanceContingency = hasNegativeBalanceContingency
      state.isFundingSourcesUnavailable = isFundingSourcesUnavailable
    },

    updateFlowInputParams(state, action) {
      const {
        productName = null,
        returnUri = null,
        cancelUri = null,
        webviewCallback = null,
      } = action.payload || {}

      state.flowInputParams = {
        productName,
        returnUri,
        cancelUri,
        webviewCallback,
      }
    },

    updateSelectedFundingInstrumentId(state, action) { // pass selected funding instrument id  
      state.selectedFundingInstrumentId = action.payload
    },

    // It ensures that when a user leaves the funding instruments flow (e.g., navigates away), 
    // all related state is cleared, so the next time the flow starts, it begins fresh.
    resetFundingInstruments(state) {
      Object.assign(state, {
        ...initialState,
      })
    },

    setToastMessage(state, action) {
      state.toastMessage = action.payload?.toastMessage
    },
    clearToastMessage(state) {
      state.toastMessage = {
        message: null,
        isSuccess: true,
      }
    },
  },
})

// Custom selectors
export const selectFlowInputParams = (state) => state.fundingInstruments.flowInputParams
export const selectFundingInstrumentIds = (state) => state.fundingInstruments.fundingInstrumentIds
export const selectSelectedFundingInstrumentId = (state) => state.fundingInstruments.selectedFundingInstrumentId
export const selectFundingInstrumentsMap = (state) => state.fundingInstruments?.fundingInstrumentsMap
export const selectSubmittedFundingInstrument = (state) => state.fundingInstruments.submittedFundingInstrument
export const selectSecondaryFundingOptions = (state) => state.fundingInstruments.secondaryFundingOptions || []
export const selectLinkFIflowQuery = (state) => state.fundingInstruments.linkFIflowQuery
export const selectStatusDetailsValues = (state) => state.fundingInstruments.statusDetailsValues
export const selectHasRiskStepUpContingency = (state) => state.fundingInstruments.hasRiskStepUpContingency
export const selectHasRiskDeclineContingency = (state) => state.fundingInstruments.hasRiskDeclineContingency
export const selectHasNegativeBalanceContingency = (state) => state.fundingInstruments.hasNegativeBalanceContingency
export const selectIsFundingSourcesUnavailable = (state) => state.fundingInstruments.isFundingSourcesUnavailable

export const selectCurrentFundingInstrument = (state) => {
  const selectedId = selectSelectedFundingInstrumentId(state)
  const instrumentsMap = selectFundingInstrumentsMap(state) || {}
  
  return selectedId && Object.keys(instrumentsMap).length > 0
    ? instrumentsMap[selectedId]
    : null
}

export const selectIsContingencyStatus = (state) => {
  const currentFI = selectCurrentFundingInstrument(state)
  if (!currentFI) return false
  // As of Sept 2025, both contingency and unconfirmed bank/card status are represented by the same contingency status
  return currentFI.status === RESOLVE_FUNDING_OPTIONS_CONTINGENCY.STATUS.CONTINGENCY
}

export const selectToastMessage = (state) => state.fundingInstruments?.toastMessage

export const {
  initFundingInstruments,
  updateFlowInputParams,
  updateSelectedFundingInstrumentId,
  resetFundingInstruments,
  setToastMessage,
  clearToastMessage,
} = fundingInstrumentsSlice.actions

export default fundingInstrumentsSlice.reducer
